/**
 * ReportWebService — browser-side report adapter.
 *
 * Owns every Registry API call that kirigami-app needs for the
 * upload → analyse → poll → detail flow documented in
 * `docs/report-upload-flow.md`. Methods return typed DTOs; callers
 * (app-level facades) map to view-model shapes as needed.
 *
 * What belongs here:
 * - REST calls to /v1/reports/* (list, create, get-with-files, delete,
 *   start-analysis, extract-text, upload/download URL, user sharing)
 * - Direct S3 XHR upload (bypasses Angular's HttpClient so the auth
 *   interceptor does NOT attach a Bearer header to presigned URLs)
 * - Status polling with configurable interval
 *
 * What does NOT belong here:
 * - Domain-level enrichment (scores, stages, priorities) → live in
 *   `domain/analysis-mappers` and app view-models
 * - Router / dialog / snackbar concerns → app layer
 */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  REGISTRY_API,
  type AddUserToReportRequest,
  type CheckCreditsResponse,
  type ConsumptionHistoryEntry,
  type CreateReportRequest,
  type ExtractTextResponse,
  type PresignedDownloadDto,
  type PresignedUploadDto,
  type RegistryReportDto,
  type ReportSectionContentDto,
  type ReportUserDto,
  type ReportWithFilesDto,
  type StartAnalysisRequest,
  type UpdateReportRequest,
  type UpdateReportSectionsContentRequest,
} from '@kirigami/data-access/registry-api-contracts';
import { Observable, catchError, map, of, switchMap, takeWhile, throwError, timer } from 'rxjs';
import { REGISTRY_API_BASE_URL } from '@kirigami/data-access/registry-api-client';
import { isProcessingInFlight } from '@kirigami/domain/analysis-rules';

/** Default polling cadence for report-with-files (matches legacy kirigami-analysis). */
const DEFAULT_POLL_INTERVAL_MS = 15_000;

// ── start-analysis refusal codes (backend contract, 2026-07-22) ──────

/** 403 — the billed plan expired; the backend refuses any further runs. */
export const REPORT_SUBSCRIPTION_EXPIRED = 'SUBSCRIPTION_EXPIRED';
/** 402 — no spendable balance at all on the billed plan + personal wallet. */
export const REPORT_INSUFFICIENT_TOKENS = 'INSUFFICIENT_TOKENS';
/** 402 — balance exists but is fully reserved by in-flight operations. */
export const REPORT_INSUFFICIENT_AVAILABLE_BALANCE = 'INSUFFICIENT_AVAILABLE_BALANCE';
/** 409 — a concurrent duplicate start for the same report. */
export const REPORT_ANALYSIS_ALREADY_RUNNING = 'ANALYSIS_ALREADY_RUNNING';
/**
 * 403 — this email's one-time Free benefit was consumed by a previous
 * (deleted) account; only a paid plan restores analysis access
 * (2026-07-23 free-plan-reuse spec).
 */
export const REPORT_FREE_PLAN_ALREADY_USED = 'FREE_PLAN_ALREADY_USED';

/**
 * User-facing message per machine-readable refusal code. The
 * INSUFFICIENT_TOKENS copy intentionally matches the legacy generic
 * 402/429 message so callers that only read `.message` see no change.
 */
const START_ANALYSIS_MESSAGES: Record<string, string> = {
  [REPORT_SUBSCRIPTION_EXPIRED]: 'Your subscription has ended. Subscribe to a plan to continue.',
  [REPORT_INSUFFICIENT_TOKENS]:
    'Not enough analysis credits to start this analysis. Please top up and try again.',
  [REPORT_INSUFFICIENT_AVAILABLE_BALANCE]:
    'Your available credits are reserved by analyses in progress. Wait for them to finish or top up.',
  [REPORT_ANALYSIS_ALREADY_RUNNING]: 'An analysis is already running for this report.',
  [REPORT_FREE_PLAN_ALREADY_USED]:
    'The Free plan is available once per user. Choose a paid plan to continue.',
};

/**
 * Typed transport error for the Registry report surface. `start-analysis`
 * signals refusals with a machine-readable `code` in the error body
 * (SUBSCRIPTION_EXPIRED / INSUFFICIENT_TOKENS / INSUFFICIENT_AVAILABLE_BALANCE
 * on 402/403, ANALYSIS_ALREADY_RUNNING on 409); consumers need the
 * status/code to react specifically (expired-plan gate, top-up prompt)
 * instead of pattern-matching messages. Extends `Error`, so callers that
 * only read `.message` keep working unchanged. Mirrors `KbApiError` in
 * `@kirigami/data-access/kb-adapters-web`.
 */
export class ReportApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ReportApiError';
  }
}

/**
 * Hard cap on poll iterations. With the default 15s interval this
 * gives ~10min before we surface a clear timeout error. Prevents the
 * UI from hanging indefinitely when the Step Function succeeds but
 * the backend never writes `processing_status: SUCCEEDED` back to
 * the report record (an observed failure mode on the PAYG flow).
 */
const DEFAULT_MAX_POLL_ATTEMPTS = 40;

/**
 * Reads the report's status. `status` is the authoritative field —
 * the registry flips it to `SUCCEEDED` when the engine finishes, and
 * that's what gates finalResult.json retrieval. `processing_status`
 * is kept only as a defensive fallback for response variants that
 * don't include `status`. Both nested (`response.report.*`) and
 * top-level shapes are tolerated per ReportWithFilesDto's "Some
 * response variants inline these fields at the top level" comment.
 */
function extractStatus(response: ReportWithFilesDto): string {
  return (
    response.report?.status ||
    response.status ||
    response.report?.processing_status ||
    response.processing_status ||
    ''
  )
    .toString()
    .toUpperCase();
}

@Injectable({ providedIn: 'root' })
export class ReportWebService {
  readonly #http = inject(HttpClient);
  readonly #baseUrl = inject(REGISTRY_API_BASE_URL);

  #url(path: string): string {
    return `${this.#baseUrl}${path}`;
  }

  // ── Reports: list / CRUD ────────────────────────────────

  /** `GET /v1/reports/` — list reports visible to the current user. */
  fetchReports(): Observable<RegistryReportDto[]> {
    return this.#http.get<RegistryReportDto[]>(this.#url(REGISTRY_API.reports.list)).pipe(
      catchError((error) => {
        // Pass through the HttpErrorResponse so callers can branch on status
        // (401 → interceptor handles redirect; 404 → empty-state UI).
        return throwError(() => error);
      }),
    );
  }

  /**
   * `POST /v1/reports/` — create an empty report record. The backend
   * requires `status` and `processing_status`; callers should default
   * them to `'DRAFT'` / `'PENDING'` (matches web-console).
   */
  createReport(payload: CreateReportRequest): Observable<RegistryReportDto> {
    return this.#http.post<RegistryReportDto>(this.#url(REGISTRY_API.reports.create), payload).pipe(
      catchError((error) => {
        console.error('[ReportWebService] createReport failed:', error);
        return throwError(() => new Error('Failed to create report.'));
      }),
    );
  }

  /** `GET /v1/reports/report-with-files/:id` — report metadata + presigned file links. */
  fetchReportWithFiles(reportId: string): Observable<ReportWithFilesDto> {
    return this.#http.get<ReportWithFilesDto>(
      this.#url(REGISTRY_API.reports.getWithFiles(reportId)),
    );
  }

  /**
   * `PUT /v1/reports/:id` — partial update of the report record
   * (title/description). The backend also rewrites the denormalized
   * `title` on every user's access row, so list views stay consistent.
   * The response body is a bare `true`; callers should ignore it.
   */
  updateReport(reportId: string, payload: UpdateReportRequest): Observable<unknown> {
    return this.#http.put<unknown>(this.#url(REGISTRY_API.reports.update(reportId)), payload).pipe(
      catchError((error) => {
        console.error('[ReportWebService] updateReport failed:', error);
        return throwError(() => new Error('Failed to update the report.'));
      }),
    );
  }

  /** `DELETE /v1/reports/:id` */
  deleteReport(reportId: string): Observable<void> {
    return this.#http.delete<void>(this.#url(REGISTRY_API.reports.delete(reportId))).pipe(
      catchError((error) => {
        console.error('[ReportWebService] deleteReport failed:', error);
        return throwError(() => new Error('Failed to delete report.'));
      }),
    );
  }

  // ── Upload flow: presigned URL + direct-to-S3 XHR ──────

  /** `POST /v1/reports/get-upload-url` — presigned S3 POST envelope. */
  getUploadUrl(
    reportId: string,
    filename: string,
    subscriptionId: string,
  ): Observable<PresignedUploadDto> {
    return this.#http
      .post<PresignedUploadDto>(this.#url(REGISTRY_API.reports.getUploadUrl), {
        report_id: reportId,
        filename,
        subscription_id: subscriptionId,
      })
      .pipe(
        catchError((error) => {
          console.error('[ReportWebService] getUploadUrl failed:', error);
          return throwError(() => new Error('Failed to prepare file upload.'));
        }),
      );
  }

  /**
   * Uploads a file directly to S3 using the presigned POST envelope.
   * Uses raw XMLHttpRequest — NOT HttpClient — so the auth interceptor
   * does not clobber S3's self-signed URL with a Bearer header.
   */
  uploadFile(presigned: PresignedUploadDto, file: File): Observable<void> {
    return new Observable<void>((observer) => {
      const formData = new FormData();

      // Form fields must come BEFORE the file (S3 policy requirement).
      Object.entries(presigned.link.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', presigned.link.url, true);
      // Intentionally no Authorization header here.

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          observer.next();
          observer.complete();
        } else {
          observer.error(new Error(`File upload failed (status ${xhr.status}).`));
        }
      };

      xhr.onerror = () => {
        observer.error(new Error('File upload failed. Please check your connection.'));
      };

      xhr.send(formData);
    });
  }

  /** `POST /v1/reports/get-download-url` — presigned GET for an original file. */
  getDownloadUrl(filename: string): Observable<PresignedDownloadDto> {
    return this.#http
      .post<PresignedDownloadDto>(this.#url(REGISTRY_API.reports.getDownloadUrl), { filename })
      .pipe(
        catchError((error) => {
          console.error('[ReportWebService] getDownloadUrl failed:', error);
          return throwError(() => new Error('Failed to prepare file download.'));
        }),
      );
  }

  /**
   * Fetches a presigned download URL for a report's first input file.
   * Convenience helper for the dashboard's "download original" action.
   */
  downloadOriginalFile(reportId: string): Observable<string> {
    return this.fetchReportWithFiles(reportId).pipe(
      switchMap((response) => {
        const inputFiles = response.report?.input_files ?? response.input_files ?? [];
        const filename = inputFiles[0];
        if (!filename) {
          return throwError(() => new Error('No original file is attached to this report.'));
        }
        return this.getDownloadUrl(filename).pipe(map((data) => data.url));
      }),
      catchError((error) => {
        console.error('[ReportWebService] downloadOriginalFile failed:', error);
        return throwError(
          () => new Error(error?.message || 'Failed to download the original file.'),
        );
      }),
    );
  }

  // ── Pre-analysis gates ─────────────────────────────────

  /**
   * `POST /v1/reports/extract-text` — pre-analysis text-extractability check.
   * Normalises the response to a single string: the backend has returned
   * `{ text }` or `{ content }` at different times; adapters tolerate both.
   */
  extractText(filename: string): Observable<string> {
    return this.#http
      .post<ExtractTextResponse | string>(this.#url(REGISTRY_API.reports.extractText), {
        filename,
      })
      .pipe(
        map((response) => {
          if (typeof response === 'string') return response;
          return response?.text || response?.content || '';
        }),
        catchError((error) => {
          console.error('[ReportWebService] extractText failed:', error);
          return throwError(
            () =>
              new Error(
                "We couldn't extract any text from this file. Scanned, encrypted, or image-only PDFs are not supported.",
              ),
          );
        }),
      );
  }

  /** `POST /v1/reports/check-credits` — advisory credit pre-flight. */
  checkCredits(subscriptionId: string, reportType = 'standard'): Observable<CheckCreditsResponse> {
    return this.#http
      .post<CheckCreditsResponse>(this.#url(REGISTRY_API.reports.checkCredits), {
        subscription_id: subscriptionId,
        report_type: reportType,
      })
      .pipe(
        catchError((error) => {
          console.error('[ReportWebService] checkCredits failed:', error);
          return throwError(() => new Error('Unable to verify credits. Please try again.'));
        }),
      );
  }

  /**
   * `GET /v1/reports/consumption-history` — the authenticated user's
   * credit-ledger movements (reserve / debit / release), newest-first.
   * Unwraps the `{ history }` envelope to the bare array and tolerates a
   * missing/empty payload so callers can render an empty state instead
   * of handling `undefined`.
   */
  getConsumptionHistory(): Observable<ConsumptionHistoryEntry[]> {
    return this.#http
      .get<{
        history: ConsumptionHistoryEntry[];
      }>(this.#url(REGISTRY_API.reports.consumptionHistory))
      .pipe(
        map((response) => response?.history ?? []),
        catchError((error) => {
          console.error('[ReportWebService] getConsumptionHistory failed:', error);
          return throwError(() => new Error('Failed to load usage history.'));
        }),
      );
  }

  // ── Analysis run + polling ─────────────────────────────

  /**
   * `PUT /v1/reports/:reportId/update-sections-content-cache` — push the
   * edited section text into the backend cache before re-analysis.
   *
   * `section_id` must match the canonical normalized form used by the
   * analysis pipeline (see `ReportService.normalizeSectionId` on the
   * app side), not the display label.
   */
  updateSectionsContentCache(
    reportId: string,
    sections: ReportSectionContentDto[],
  ): Observable<unknown> {
    const payload: UpdateReportSectionsContentRequest = { sections };
    return this.#http
      .put<unknown>(this.#url(REGISTRY_API.reports.updateSectionsContent(reportId)), payload)
      .pipe(
        catchError((error) => {
          console.error('[ReportWebService] updateSectionsContentCache failed:', error);
          return throwError(() => new Error('Failed to save section edits.'));
        }),
      );
  }

  /**
   * `POST /v1/reports/start-analysis` — browser shell analysis entry point.
   *
   * The backend refuses runs with a machine-readable `code` in the error
   * body: 403 SUBSCRIPTION_EXPIRED (billed plan expired), 402
   * INSUFFICIENT_TOKENS / INSUFFICIENT_AVAILABLE_BALANCE (no balance /
   * balance reserved by in-flight operations), 409 ANALYSIS_ALREADY_RUNNING
   * (concurrent duplicate start; an idempotent replay is a 200
   * `{status: "duplicate"}` and never lands here). Each is rethrown as a
   * {@link ReportApiError} carrying `{status, code}` plus a code-specific
   * message, so callers can branch on the code while message-only
   * consumers keep working. Code-less 402/429 responses (pre-cutover
   * backends) keep the legacy not-enough-credits message.
   */
  startAnalysis(request: StartAnalysisRequest): Observable<unknown> {
    return this.#http.post<unknown>(this.#url(REGISTRY_API.reports.startAnalysis), request).pipe(
      catchError((error) => {
        console.error('[ReportWebService] startAnalysis failed:', error);
        const status: number = error?.status ?? 0;
        const body = error?.error as { detail?: unknown; code?: unknown } | undefined;
        const code = typeof body?.code === 'string' ? body.code : undefined;
        const mapped = code ? START_ANALYSIS_MESSAGES[code] : undefined;
        if (mapped) {
          return throwError(() => new ReportApiError(mapped, status, code));
        }
        // Legacy fallback: a 402/429 without a recognised code is still a
        // credit refusal — surface the credit-specific message so the UI
        // can prompt a top-up.
        if (status === 402 || status === 429) {
          return throwError(
            () =>
              new ReportApiError(START_ANALYSIS_MESSAGES[REPORT_INSUFFICIENT_TOKENS], status, code),
          );
        }
        return throwError(() => new ReportApiError('Failed to start analysis.', status, code));
      }),
    );
  }

  /**
   * Polls `report-with-files` until the report's processing_status
   * leaves PENDING / PROCESSING. First fetch fires immediately
   * (`timer(0, intervalMs)`) so callers do not wait a full interval
   * when the backend is already done. The final emission is the
   * terminal-state response (inclusive `takeWhile`).
   *
   * Errors with a clear diagnostic message after `maxAttempts` polls
   * if the status is still in-progress — this is the failure mode
   * where the Step Function succeeded but the backend never wrote
   * `processing_status: SUCCEEDED` back to the report record.
   *
   * Each poll logs the resolved status so the cause is visible in
   * devtools.
   */
  pollReportStatus(
    reportId: string,
    intervalMs: number = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts: number = DEFAULT_MAX_POLL_ATTEMPTS,
  ): Observable<ReportWithFilesDto> {
    let attempt = 0;
    return timer(0, intervalMs).pipe(
      switchMap(() => this.fetchReportWithFiles(reportId)),
      map((response) => {
        attempt++;
        const status = extractStatus(response);
        console.info('[ReportWebService] poll', {
          reportId,
          attempt,
          status: status || 'UNKNOWN',
          hasFinalResultLink: !!response.final_result_link,
        });
        if (attempt >= maxAttempts && isProcessingInFlight(status)) {
          const elapsedSec = Math.round((attempt * intervalMs) / 1000);
          throw new Error(
            `Report ${reportId} stuck in ${status} after ${attempt} polls (~${elapsedSec}s). ` +
              `The Step Function likely succeeded but the report record's processing_status was ` +
              `not updated by the workflow's final state. Check the writer step.`,
          );
        }
        return response;
      }),
      takeWhile((response) => isProcessingInFlight(extractStatus(response)), true),
    );
  }

  // ── Report sharing ─────────────────────────────────────

  /**
   * `POST /v1/reports/:id/add-user` — invite another user to a report.
   * Surfaces the 4xx `detail` field when present so callers can display
   * the backend-authoritative reason (already-shared, user-not-found,
   * forbidden) rather than a generic string.
   */
  addUserToReport(reportId: string, payload: AddUserToReportRequest): Observable<ReportUserDto> {
    return this.#http
      .post<ReportUserDto>(this.#url(REGISTRY_API.reports.addUser(reportId)), payload)
      .pipe(
        catchError((error) => {
          console.error('[ReportWebService] addUserToReport failed:', error);
          const detail =
            (error?.error && typeof error.error === 'object' && error.error.detail) ||
            error?.message;
          return throwError(() => new Error(detail || 'Failed to send invitation.'));
        }),
      );
  }

  /** `DELETE /v1/reports/:id/delete-user/:userPk` — revoke user access. */
  removeUserFromReport(reportId: string, userPk: string): Observable<void> {
    return this.#http
      .delete<void>(this.#url(REGISTRY_API.reports.deleteUser(reportId, userPk)))
      .pipe(
        catchError((error) => {
          console.error('[ReportWebService] removeUserFromReport failed:', error);
          const detail =
            (error?.error && typeof error.error === 'object' && error.error.detail) ||
            error?.message;
          return throwError(() => new Error(detail || 'Failed to remove user access.'));
        }),
      );
  }

  /** `GET /v1/reports/:id/users` — list collaborators on a report. */
  fetchReportUsers(reportId: string): Observable<ReportUserDto[]> {
    return this.#http.get<ReportUserDto[]>(this.#url(REGISTRY_API.reports.getUsers(reportId))).pipe(
      catchError((error) => {
        console.error('[ReportWebService] fetchReportUsers failed:', error);
        return throwError(() => new Error('Failed to load shared users.'));
      }),
    );
  }

  // ── Analysis JSON resolution ───────────────────────────

  /**
   * Resolves the analysis JSON for a completed report. Follows the
   * documented precedence: presigned `final_result_link` → embedded
   * `report.final_result` → response itself when it already looks like
   * analysis data. Throws when none of those are available.
   */
  loadAnalysisJson(reportId: string): Observable<unknown> {
    return this.loadReportWithAnalysis(reportId).pipe(map(({ analysis }) => analysis));
  }

  /**
   * Same resolution as {@link loadAnalysisJson} but emits both the
   * analysis JSON and the report record (containing `owner`, `title`,
   * etc.). Use this when the caller needs metadata fields the analysis
   * JSON does not carry — `owner` in particular drives owner-only edit
   * gating in the report-detail surface.
   *
   * Single HTTP round-trip (the embedded record is reused for analysis
   * resolution).
   */
  loadReportWithAnalysis(
    reportId: string,
  ): Observable<{ report: RegistryReportDto | undefined; analysis: unknown }> {
    return this.fetchReportWithFiles(reportId).pipe(
      switchMap((response) => {
        const record = response.report;
        const status = (record?.processing_status || record?.status || '').toString();
        const branch = response.final_result_link
          ? 'presigned-link'
          : record?.final_result !== undefined && record?.final_result !== null
            ? 'embedded'
            : response.sections && response.questions
              ? 'inline'
              : 'none';
        console.info('[ReportWebService] report-with-files resolved', {
          reportId,
          status,
          branch,
          finalResultLink: response.final_result_link ?? null,
          embeddedKeys: record?.final_result
            ? Object.keys(record.final_result as Record<string, unknown>)
            : null,
        });

        let analysis$: Observable<unknown>;
        if (response.final_result_link) {
          analysis$ = this.#http
            .get<unknown>(response.final_result_link, { observe: 'response' })
            .pipe(
              map((res) => {
                const body = res.body;
                console.info('[ReportWebService] finalResult fetched', {
                  reportId,
                  url: response.final_result_link,
                  status: res.status,
                  size: res.headers.get('content-length'),
                  topKeys: body && typeof body === 'object' ? Object.keys(body) : null,
                });
                if (!body || typeof body !== 'object') {
                  throw new Error(
                    `finalResult URL returned an empty or non-object body (status ${res.status}). Backend probably wrote to a different key than the report-with-files presigner is looking for. URL: ${response.final_result_link}`,
                  );
                }
                return body;
              }),
            );
        } else if (
          response.report?.final_result !== undefined &&
          response.report?.final_result !== null
        ) {
          analysis$ = of(response.report.final_result);
        } else if (response.sections && response.questions) {
          analysis$ = of(response as unknown);
        } else {
          return throwError(
            () =>
              new Error(
                `No analysis data available for report ${reportId} (status="${status}"). report-with-files returned no final_result_link, no embedded final_result, and no inline sections/questions. Check the writer step's S3 key vs the presigner's S3 key (both should be reports/<id>/finalResult.json).`,
              ),
          );
        }
        return analysis$.pipe(map((analysis) => ({ report: record, analysis })));
      }),
      catchError((error) => {
        console.error('[ReportWebService] loadReportWithAnalysis failed:', error);
        return throwError(
          () =>
            new Error(
              error?.status === 404
                ? 'Report not found. Please check the report ID.'
                : error?.message || 'Unable to load the report. Please try again later.',
            ),
        );
      }),
    );
  }
}
